import { logError } from "@lib/logger";

const HEALTH_CHECK_PATH = '/api/health/';
const WEBSOCKET_HANDSHAKE_TIMEOUT_MS = 30_000;

export enum WebSocketIssueCategory {
	NETWORK_UNREACHABLE = 'network_unreachable',
	SERVER_ERROR = 'server_error',
	HANDSHAKE_BLOCKED = 'handshake_blocked',
	CONNECTION_DROPPED = 'connection_dropped',
	DEGRADED = 'degraded',
}

const WEBSOCKET_ISSUE_MESSAGE_BY_CATEGORY: Record<WebSocketIssueCategory, string> = {
	[WebSocketIssueCategory.NETWORK_UNREACHABLE]: 'WebSocket issue: client has no network connectivity',
	[WebSocketIssueCategory.SERVER_ERROR]: 'WebSocket issue: server health check failed',
	[WebSocketIssueCategory.HANDSHAKE_BLOCKED]: 'WebSocket issue: handshake blocked despite working network',
	[WebSocketIssueCategory.CONNECTION_DROPPED]: 'WebSocket issue: live connection dropped unexpectedly',
	[WebSocketIssueCategory.DEGRADED]: 'WebSocket issue: degraded connection quality',
};

export type ConnectionCloseEventInfo = {
	closeCode?: number;
	closeReason?: string;
	wasClean?: boolean;
	timeSinceLastPongMs?: number;
	pingRoundTripMs?: number;
	reconnectCount?: number;
};

type DiagnosticTimings = {
	connectivityDurationMs?: number;
	connectivityHttpStatus?: number;
	connectivityError?: string;
	wsHandshakeDurationMs?: number;
	wsPingResponseTime?: number;
	wsError?: string;
	wsTimeout?: boolean;
};

type DiagnosticIssue = {
	issue: string;
	suggestion: string;
};

export class WebSocketDiagnostics {
	private readonly url: string;
	private readonly closeEventInfo: ConnectionCloseEventInfo;
	private diagnosticTimings: DiagnosticTimings = {};

	constructor(url: string, connectionEventContext: ConnectionCloseEventInfo = {}) {
		this.url = url;
		this.closeEventInfo = connectionEventContext;
	}

	async run(): Promise<void> {
		await this._testServerConnectivity();
		await this._testWebSocket();
		this._report();
	}

	private async _testServerConnectivity(): Promise<void> {
		const urlObj = new URL(this.url);
		const httpUrl = `${urlObj.protocol === 'wss:' ? 'https:' : 'http:'}//${urlObj.host}${HEALTH_CHECK_PATH}`;
		const start = performance.now();
		try {
			const response = await fetch(httpUrl, { method: 'GET' });
			this.diagnosticTimings.connectivityDurationMs = performance.now() - start;
			if (!response.ok) {
				this.diagnosticTimings.connectivityHttpStatus = response.status;
			}
		} catch (error) {
			this.diagnosticTimings.connectivityDurationMs = performance.now() - start;
			this.diagnosticTimings.connectivityError = error instanceof Error ? error.message : String(error);
		}
	}

	private _testWebSocket(): Promise<void> {
		return new Promise((resolve: () => void) => {
			const webSocketTestsStartTime = performance.now();
			let settled = false;
			let ws: WebSocket;

			const finish = (): void => {
				if (!settled) {
					settled = true;
					resolve();
				}
			};

			const connectionTimeout = setTimeout((): void => {
				this.diagnosticTimings.wsTimeout = true;
				this.diagnosticTimings.wsError = 'Couldn\'t connect to ws for 30 seconds';
				ws?.close(4000, 'Diagnostic timeout');
				finish();
			}, WEBSOCKET_HANDSHAKE_TIMEOUT_MS);

			try {
				ws = new WebSocket(this.url);

				ws.onopen = (): void => {
					clearTimeout(connectionTimeout);
					this.diagnosticTimings.wsHandshakeDurationMs = performance.now() - webSocketTestsStartTime;
					const pingStart = performance.now();
					ws.send(JSON.stringify({ type: 'ping', timestamp: pingStart }));
					ws.onmessage = (): void => {
						this.diagnosticTimings.wsPingResponseTime = performance.now() - pingStart;
						ws.close();
						finish();
					};
				};

				ws.onerror = (): void => {
					clearTimeout(connectionTimeout);
					if (!settled) this.diagnosticTimings.wsError = 'Connection error'; // do we actually need settled and cant we show the real error? 
					finish();
				};

				ws.onclose = (event: CloseEvent): void => {
					clearTimeout(connectionTimeout);
					if (!settled && !this.diagnosticTimings.wsHandshakeDurationMs) {
						this.diagnosticTimings.wsError = `Connection closed: ${event.code} ${event.reason}`;
					}
					finish();
				};
			} catch (error) {
				clearTimeout(connectionTimeout);
				this.diagnosticTimings.wsError = error instanceof Error ? error.message : String(error);
				finish();
			}
		});
	}

	private _categorizeIssue(): WebSocketIssueCategory {
		if (this.diagnosticTimings.connectivityError) {
			return WebSocketIssueCategory.NETWORK_UNREACHABLE;
		}
		if (this.diagnosticTimings.connectivityHttpStatus !== undefined) {
			return WebSocketIssueCategory.SERVER_ERROR;
		}
		if (this.diagnosticTimings.wsTimeout) {
			return WebSocketIssueCategory.HANDSHAKE_BLOCKED;
		}
		if (this.closeEventInfo.wasClean === false) {
			return WebSocketIssueCategory.CONNECTION_DROPPED;
		}
		return WebSocketIssueCategory.DEGRADED;
	}

	private _report(): void {
		const issues: DiagnosticIssue[] = [];

		if (this.diagnosticTimings.connectivityError) {
			issues.push({ issue: 'Basic connectivity failed', suggestion: 'Check network connection and server availability' });
		}
		if (this.diagnosticTimings.connectivityHttpStatus !== undefined) {
			issues.push({ issue: 'Health check returned a non-success status', suggestion: 'Check server availability and health endpoint' });
		}
		if (this.diagnosticTimings.connectivityDurationMs !== undefined && this.diagnosticTimings.connectivityDurationMs > 5000) {
			issues.push({ issue: 'High network latency', suggestion: 'Server may be geographically distant or network congested' });
		}
		if (this.diagnosticTimings.wsTimeout) {
			issues.push({ issue: 'WebSocket connection timed out', suggestion: 'Check firewall rules, proxy settings, and server WebSocket support' });
		}
		if (this.diagnosticTimings.wsHandshakeDurationMs !== undefined && this.diagnosticTimings.wsHandshakeDurationMs > 10_000) {
			issues.push({ issue: 'Slow WebSocket handshake', suggestion: 'Server may be overloaded or SSL handshake is slow' });
		}
		if (this.diagnosticTimings.wsPingResponseTime !== undefined && this.diagnosticTimings.wsPingResponseTime > 1000) {
			issues.push({ issue: 'High message latency', suggestion: 'Network path has high latency or server processing is slow' });
		}

		if (issues.length !== 0) {
			const category = this._categorizeIssue();
			logError(WEBSOCKET_ISSUE_MESSAGE_BY_CATEGORY[category], {
				category,
				url: this.url,
				issues: issues.map((i) => `${i.issue}: ${i.suggestion}`).join(' | '),
				navigator_online: navigator.onLine,
				document_visibility_state: document.visibilityState,
				time_since_last_pong_ms: this.closeEventInfo.timeSinceLastPongMs ?? null,
				ping_round_trip_ms: this.closeEventInfo.pingRoundTripMs ?? this.diagnosticTimings.wsPingResponseTime ?? null,
				reconnect_count: this.closeEventInfo.reconnectCount ?? null,
				ws_close_code: this.closeEventInfo.closeCode ?? null,
				ws_close_reason: this.closeEventInfo.closeReason ?? null,
				ws_was_clean: this.closeEventInfo.wasClean ?? null,
				connectivity_http_status: this.diagnosticTimings.connectivityHttpStatus ?? null,
				connectivity_duration_ms: this.diagnosticTimings.connectivityDurationMs ?? null,
				connectivity_error: this.diagnosticTimings.connectivityError ?? null,
				ws_handshake_duration_ms: this.diagnosticTimings.wsHandshakeDurationMs ?? null,
				ws_error: this.diagnosticTimings.wsError ?? null,
				ws_timeout: this.diagnosticTimings.wsTimeout ?? false,
			});
		}
	}
}
